import React, { useState, useEffect, useMemo } from "react";
import {
  Table, Tabs, Modal, Card, Tag, Button, Avatar, Divider, Row, Col,
  Statistic, Progress, Badge, Space, Typography, Alert, message,
  Input, Tooltip, Select, DatePicker, Layout, Empty, Timeline, Popconfirm, ConfigProvider
} from "antd";

import {
  TeamOutlined, CheckCircleFilled, SearchOutlined, LoginOutlined,
  LogoutOutlined, EditOutlined, CalendarOutlined, ClockCircleOutlined,
  UserOutlined, ArrowUpOutlined, FilterOutlined, SyncOutlined,
  InfoCircleOutlined, BarChartOutlined, HistoryOutlined, RiseOutlined,
  SafetyCertificateOutlined, MailOutlined, SendOutlined, DownloadOutlined,
  DashboardOutlined, MoreOutlined
} from "@ant-design/icons";

import axios from "axios";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Content, Header } = Layout;

const DeveloperAttendanceSystem = () => {
  // --- States ---
  const [developers, setDevelopers] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selectedDeveloper, setSelectedDeveloper] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const [attendanceData, setAttendanceData] = useState({ present: [], absent: [] });
  const [filteredPresent, setFilteredPresent] = useState([]);
  const [filteredAbsent, setFilteredAbsent] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  // Filters
  const [pFilterMonth, setPFilterMonth] = useState(null);
  const [pFilterDates, setPFilterDates] = useState([]);
  const [aFilterMonth, setAFilterMonth] = useState(null);
  const [aFilterDates, setAFilterDates] = useState([]);

  const [editingAbsent, setEditingAbsent] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);

  const [stats, setStats] = useState({
    presentCount: 0,
    absentCount: 0,
    averageHours: 0,
    productivity: 0,
    totalDays: 0,
  });

  // --- Effects ---
  useEffect(() => {
    fetchDevelopers();
  }, []);

  // --- API Functions ---
  const fetchDevelopers = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem("token");
      const response = await axios.get(
        "http://localhost:5890/adminperson/developers",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDevelopers(response.data);
    } catch (error) {
      message.error("Failed to load developers");
    } finally {
      setLoading(false);
    }
  };

  const openAttendanceModal = (dev) => {
    setSelectedDeveloper(dev);
    setModalVisible(true);
    fetchDeveloperAttendance(dev);
  };

  const fetchDeveloperAttendance = async (developer) => {
    try {
      setAttendanceLoading(true);
      const token = sessionStorage.getItem("token");
      const response = await axios.get(
        `http://localhost:5890/developer/attendances/${developer.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const presentData = response.data.attendances?.filter(item => item.developer_status === "Present") || [];
      const absentData = response.data.attendances?.filter(item => item.developer_status === "Absent") || [];

      setAttendanceData({ present: presentData, absent: absentData });
      setFilteredPresent(presentData);
      setFilteredAbsent(absentData);
      calculateAttendanceStats(presentData, absentData);
    } catch (error) {
      message.error("Attendance loading failed");
    } finally {
      setAttendanceLoading(false);
    }
  };

  const calculateAttendanceStats = (present, absent) => {
    let totalHours = 0;
    present.forEach((r) => {
      if (r.logout_out_time && r.login_in_time) {
        totalHours += dayjs(r.logout_out_time).diff(dayjs(r.login_in_time), "hour", true);
      }
    });

    const avg = present.length > 0 ? totalHours / present.length : 0;
    setStats({
      presentCount: present.length,
      absentCount: absent.length,
      totalDays: present.length + absent.length,
      averageHours: avg.toFixed(1),
      productivity: Math.min(100, Math.round((avg / 8) * 100)),
    });
  };

  const sendEmailReport = async () => {
    setEmailSending(true);
    try {
        // Mocking API call for demo purposes
        await new Promise(resolve => setTimeout(resolve, 1500));
        message.success(`Performance report dispatched to ${selectedDeveloper.developerName}`);
    } catch (error) {
        message.error("Email service unavailable");
    } finally {
        setEmailSending(false);
    }
  };

  const updateAbsentStatus = async (record, newStatus) => {
    try {
      setUpdateLoading(true);
      const token = sessionStorage.getItem("token");
      const updateData = {
        employee_ID: record.employee_id || selectedDeveloper?.developerid,
        date: record.absentdatefrom || record.date,
        status: "absent",
        absentdetailsstatus: newStatus,
      };

      await axios.post(
        "http://localhost:5890/attendances/updatestaus",
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      message.success("Record updated");
      fetchDeveloperAttendance(selectedDeveloper);
      setEditingAbsent(null);
    } catch (error) {
      message.error("Update failed");
    } finally {
      setUpdateLoading(false);
    }
  };

  const mainFilteredDevelopers = useMemo(() => {
    return developers.filter(dev => {
      const matchesSearch = dev.developerName.toLowerCase().includes(searchText.toLowerCase()) ||
                          dev.developerid.toLowerCase().includes(searchText.toLowerCase());
      const matchesStatus = statusFilter === "all" || (statusFilter === "active" && dev.designation);
      return matchesSearch && matchesStatus;
    });
  }, [developers, searchText, statusFilter]);

  const handleFilter = (type) => {
    if (type === 'present') {
      let data = [...attendanceData.present];
      if (pFilterMonth) data = data.filter(item => dayjs(item.LoginDate).format("YYYY-MM") === pFilterMonth);
      if (pFilterDates?.length === 2) {
        data = data.filter(item => dayjs(item.LoginDate).isBetween(pFilterDates[0], pFilterDates[1], 'day', '[]'));
      }
      setFilteredPresent(data);
    } else {
      let data = [...attendanceData.absent];
      if (aFilterMonth) data = data.filter(item => dayjs(item.absentdatefrom || item.date).format("YYYY-MM") === aFilterMonth);
      if (aFilterDates?.length === 2) {
        data = data.filter(item => dayjs(item.absentdatefrom || item.date).isBetween(aFilterDates[0], aFilterDates[1], 'day', '[]'));
      }
      setFilteredAbsent(data);
    }
  };

  const resetFilters = (type) => {
    if (type === 'present') {
      setPFilterMonth(null);
      setPFilterDates([]);
      setFilteredPresent(attendanceData.present);
    } else {
      setAFilterMonth(null);
      setAFilterDates([]);
      setFilteredAbsent(attendanceData.absent);
    }
  };

  const FilterBar = ({ monthValue, onMonthChange, dateValue, onDateChange, onApply, onReset }) => (
    <div className="glass-card-filter">
      <Row gutter={[12, 12]} align="middle">
        <Col xs={24} sm={8} lg={6}>
          <Select
            placeholder="Filter by Month"
            value={monthValue}
            onChange={onMonthChange}
            style={{ width: '100%' }}
            allowClear
          >
            {[...Array(12)].map((_, i) => (
              <Option key={i} value={dayjs().month(i).format("YYYY-MM")}>
                {dayjs().month(i).format("MMMM")}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={10} lg={12}>
          <RangePicker value={dateValue} onChange={onDateChange} style={{ width: '100%' }} />
        </Col>
        <Col xs={24} sm={6} lg={6}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button type="primary" onClick={onApply} icon={<FilterOutlined />} block>Filter</Button>
            <Button onClick={onReset} icon={<SyncOutlined />} />
          </Space>
        </Col>
      </Row>
    </div>
  );

  return (
    <Layout className="main-layout">
     

      <Content className="main-content">
        <div className="content-container">
          
          {/* Dashboard Title & Top Actions */}
          <div className="section-header">
            <Row gutter={[16, 16]} align="middle" justify="space-between">
              <Col xs={24} md={12}>
                <Title level={2} style={{ marginBottom: 4 }}>Attendance Intelligence</Title>
                <Text type="secondary">Real-time monitoring and performance tracking for development teams.</Text>
              </Col>
              <Col xs={24} md={12} className="text-right-md">
                <Space wrap>
                    <Input
                        placeholder="Search ID/Name"
                        prefix={<SearchOutlined />}
                        style={{ width: 220, borderRadius: 8 }}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                    <Select defaultValue="all" style={{ width: 130 }} onChange={setStatusFilter}>
                        <Option value="all">All Status</Option>
                        <Option value="active">Active Only</Option>
                    </Select>
                    <Button type="primary" shape="round" icon={<SyncOutlined />} onClick={fetchDevelopers}>Sync</Button>
                </Space>
              </Col>
            </Row>
          </div>

          {/* KPI Dashboard */}
          <Row gutter={[20, 20]} className="kpi-row">
            {[
              { title: 'Fleet Size', val: developers.length, icon: <TeamOutlined />, color: '#1890ff', bg: '#e6f7ff' },
              { title: 'On Duty', val: attendanceData.present.length || 0, icon: <CheckCircleFilled />, color: '#52c41a', bg: '#f6ffed' },
              { title: 'Leaves Today', val: attendanceData.absent.length || 0, icon: <InfoCircleOutlined />, color: '#faad14', bg: '#fff7e6' },
              { title: 'Health Index', val: '94.2%', icon: <RiseOutlined />, color: '#722ed1', bg: '#f9f0ff' }
            ].map((s, i) => (
              <Col xs={12} sm={12} lg={6} key={i}>
                <Card className="kpi-card-v2" bordered={false}>
                  <div className="kpi-icon-wrapper" style={{ backgroundColor: s.bg, color: s.color }}>
                    {s.icon}
                  </div>
                  <Statistic 
                    title={<Text strong type="secondary">{s.title}</Text>} 
                    value={s.val} 
                    valueStyle={{ fontWeight: 800, fontSize: '1.8rem' }}
                  />
                </Card>
              </Col>
            ))}
          </Row>

          {/* Main Data Table */}
          <Card bordered={false} className="table-card-wrapper">
            <Table
              dataSource={mainFilteredDevelopers}
              loading={loading}
              rowKey="id"
              scroll={{ x: 800 }}
              pagination={{ pageSize: 8, showSizeChanger: false }}
              columns={[
                {
                  title: 'DEVELOPER PROFILE',
                  fixed: 'left',
                  render: (_, r) => (
                    <Space size="middle">
                      <Avatar 
                        src={r.profileImg} 
                        size={48} 
                        style={{ border: '2px solid #f0f0f0' }}
                        icon={<UserOutlined />} 
                      />
                      <div>
                        <Text strong style={{ fontSize: 15 }}>{r.developerName}</Text>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>UID: {r.developerid}</div>
                      </div>
                    </Space>
                  )
                },
                {
                  title: 'DESIGNATION',
                  dataIndex: 'designation',
                  render: (t) => <Tag color="blue" className="capsule-tag">{t || 'Software Engineer'}</Tag>
                },
                {
                  title: 'CONNECT',
                  render: (_, r) => (
                    <Tooltip title="Send Direct Message">
                        <Button type="link" icon={<MailOutlined />} size="small">
                            {r.developerid}@pemixcels.com
                        </Button>
                    </Tooltip>
                  )
                },
                {
                  title: 'ANALYTICS',
                  align: 'center',
                  render: (_, r) => (
                    <Button 
                      icon={<BarChartOutlined />} 
                      onClick={() => openAttendanceModal(r)}
                      type="primary"
                      ghost
                      className="action-pill-btn"
                    >
                      Metrics
                    </Button>
                  )
                }
              ]}
            />
          </Card>
        </div>
      </Content>

      {/* Advanced Analytics Modal */}
      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={1100}
        centered
        className="modern-analytics-modal"
        destroyOnClose
      >
        <div className="modal-responsive-container">
            <div className="modal-sidebar-info">
                <div className="profile-header">
                    <Avatar size={90} icon={<UserOutlined />} className="sidebar-avatar" />
                    <Title level={3} style={{ margin: '16px 0 4px' }}>{selectedDeveloper?.developerName}</Title>
                    <Tag color="cyan">{selectedDeveloper?.designation}</Tag>
                </div>
                
                <Divider />
                
                <div className="stats-grid">
                    <div className="stat-item">
                        <Text type="secondary" size="small">Productivity</Text>
                        <Title level={4} style={{ margin: 0 }}>{stats.productivity}%</Title>
                        <Progress percent={stats.productivity} strokeColor="#52c41a" size="small" showInfo={false} />
                    </div>
                    <div className="stat-item">
                        <Text type="secondary" size="small">Avg. Session</Text>
                        <Title level={4} style={{ margin: 0 }}>{stats.averageHours}h</Title>
                    </div>
                </div>

                <div className="sidebar-footer-actions">
                    <Button 
                        block 
                        type="primary" 
                        size="large"
                        icon={<SendOutlined />} 
                        loading={emailSending}
                        onClick={sendEmailReport}
                    >
                        Send Report
                    </Button>
                    <Button block size="large" icon={<DownloadOutlined />} style={{ marginTop: 12 }}>Export Data</Button>
                </div>
            </div>

            <div className="modal-data-area">
                <Tabs defaultActiveKey="1" type="card" className="styled-tabs">
                    <Tabs.TabPane tab="Work Logs" key="1">
                        <FilterBar 
                            monthValue={pFilterMonth} onMonthChange={setPFilterMonth}
                            dateValue={pFilterDates} onDateChange={setPFilterDates}
                            onApply={() => handleFilter('present')} onReset={() => resetFilters('present')}
                        />
                        <Table
                            dataSource={filteredPresent}
                            loading={attendanceLoading}
                            pagination={{ pageSize: 5 }}
                            size="middle"
                            columns={[
                                { title: 'Date', dataIndex: 'LoginDate', render: d => <Text strong>{dayjs(d).format("DD MMM YYYY")}</Text> },
                                { title: 'Clock In', dataIndex: 'login_time_string', render: t => <Tag icon={<ClockCircleOutlined />} color="default">{t}</Tag> },
                                { title: 'Clock Out', dataIndex: 'logout_time_string', render: t => t ? <Tag color="blue">{t}</Tag> : <Badge status="processing" text="Online" /> },
                                { title: 'Type', render: () => <Badge status="success" text="On-site" /> }
                            ]}
                        />
                    </Tabs.TabPane>
                    <Tabs.TabPane tab="Leave Management" key="2">
                         <FilterBar 
                            monthValue={aFilterMonth} onMonthChange={setAFilterMonth}
                            dateValue={aFilterDates} onDateChange={setAFilterDates}
                            onApply={() => handleFilter('absent')} onReset={() => resetFilters('absent')}
                        />
                        <Table
                            dataSource={filteredAbsent}
                            loading={attendanceLoading}
                            pagination={{ pageSize: 5 }}
                            columns={[
                                { title: 'Date', render: (_, r) => dayjs(r.absentdatefrom || r.date).format("DD MMM YYYY") },
                                { title: 'Category', dataIndex: 'absendtype', render: t => <Tag color="orange">{t || 'Unspecified'}</Tag> },
                                { 
                                    title: 'Approval Status', 
                                    render: (_, record) => {
                                        const status = record.absentdetailsstatus || "pending";
                                        return editingAbsent?._id === record._id ? (
                                            <Select size="small" defaultValue={status} onChange={(v) => updateAbsentStatus(record, v)} style={{ width: 110 }}>
                                                <Option value="pending">Pending</Option>
                                                <Option value="approved">Approved</Option>
                                            </Select>
                                        ) : (
                                            <Space>
                                                <Tag color={status === 'approved' ? 'success' : 'processing'}>{status.toUpperCase()}</Tag>
                                                <Button type="text" icon={<EditOutlined />} onClick={() => setEditingAbsent(record)} />
                                            </Space>
                                        );
                                    }
                                }
                            ]}
                        />
                    </Tabs.TabPane>
                </Tabs>
            </div>
        </div>
      </Modal>

      <style jsx global>{`
        /* Root & Layout */
        .main-layout { background: #f0f2f5; min-height: 100vh; }
        .app-header { background: #001529; padding: 0 24px; position: sticky; top: 0; z-index: 1000; height: 64px; }
        .header-container { max-width: 1400px; margin: 0 auto; display: flex; justify-content: space-between; align-items: center; }
        .logo-section { display: flex; align-items: center; gap: 12px; }
        .brand-logo { width: 36px; height: 36px; background: #1890ff; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-size: 20px; }
        
        .main-content { padding: 32px 24px; }
        .content-container { max-width: 1400px; margin: 0 auto; }
        
        /* Typography & Headers */
        .section-header { margin-bottom: 32px; }
        .text-right-md { text-align: right; }
        @media (max-width: 768px) { .text-right-md { text-align: left; } .hide-mobile { display: none; } }
        
        /* KPI Cards */
        .kpi-row { margin-bottom: 32px; }
        .kpi-card-v2 { border-radius: 16px; transition: all 0.3s ease; box-shadow: 0 4px 12px rgba(0,0,0,0.03); }
        .kpi-card-v2:hover { transform: translateY(-5px); box-shadow: 0 8px 24px rgba(0,0,0,0.08); }
        .kpi-icon-wrapper { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 22px; margin-bottom: 16px; }
        
        /* Main Table */
        .table-card-wrapper { border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.04); padding: 8px; }
        .capsule-tag { border-radius: 20px; padding: 2px 12px; font-weight: 500; }
        .action-pill-btn { border-radius: 20px; }
        
        /* Modal & Tabs */
        .modern-analytics-modal .ant-modal-content { border-radius: 24px; overflow: hidden; padding: 0; }
        .modal-responsive-container { display: flex; height: 75vh; }
        @media (max-width: 992px) { .modal-responsive-container { flex-direction: column; height: auto; } }
        
        .modal-sidebar-info { width: 300px; background: #fafafa; border-right: 1px solid #f0f0f0; padding: 40px 24px; text-align: center; flex-shrink: 0; }
        @media (max-width: 992px) { .modal-sidebar-info { width: 100%; border-right: none; border-bottom: 1px solid #f0f0f0; } }
        
        .modal-data-area { flex: 1; padding: 32px; overflow-y: auto; background: white; }
        .stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; text-align: left; margin: 24px 0; }
        
        .glass-card-filter { background: #f8f9fa; padding: 16px; border-radius: 12px; margin-bottom: 24px; border: 1px solid #eee; }
        .styled-tabs .ant-tabs-nav::before { border-bottom: none; }
        
        .ant-table-thead > tr > th { background: #fafafa !important; color: #555 !important; font-size: 12px !important; text-transform: uppercase; letter-spacing: 0.5px; }
      `}</style>
    </Layout>
  );
};

export default DeveloperAttendanceSystem;